import type * as Y from "yjs"
import {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useBlocker, useNavigate } from "@tanstack/react-router"
import OrderedList from "@tiptap/extension-ordered-list"
import { IconBookOpen4Outline18 as BookOpenIcon } from "nucleo-ui-outline-18"

import type {
	HelpCenterArticle,
	HelpCenterArticleTranslation,
	HelpCenterGroup,
	HelpCenterGroupTranslation,
} from "@productlane/zero"
import { isArticleCoverHidden } from "@productlane/lib/src/article-cover"
import { generateHeadlineKey } from "@productlane/lib/src/articles"
import { cn } from "@productlane/lib/src/browser"
import { ARTICLE_SUMMARY_MAX_LENGTH } from "@productlane/lib/src/help-center/article-summary"
import { computeProposalSteps } from "@productlane/lib/src/prosemirror-diff"
import { docsCollabName } from "@productlane/lib/src/sockets/docs-collab"
import { ShiftEnterParagraph } from "@productlane/lib/src/tiptap/extensions"
import { Textarea } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { HelpCenterArticleFormData } from "./help-center-article-schema"
import type { JSONValue } from "@rocicorp/zero"
import type { Editor } from "@tiptap/react"
import { CoverImage } from "@/components/cover-image"
import { DefaultArticleCover } from "@/components/default-article-cover"
import {
	isRemoteChange,
	isSeeded,
	isSessionLeader,
	rememberSaved,
	seedSharedDoc,
	wasSavedBySession,
	watchSeedRace,
} from "@/components/editor/collaboration/shared-doc"
import { sharedDocExtensions } from "@/components/editor/collaboration/shared-doc-extensions"
import { useSharedDoc } from "@/components/editor/collaboration/use-shared-doc"
import { DefaultEditor } from "@/components/editor/default-editor"
import { isDiscreteEditorChange } from "@/components/editor/discrete-editor-change"
import {
	cursorColor,
	RemoteCursors,
} from "@/components/editor/extensions/remote-cursors"
import { useArticleLinkPills } from "@/components/editor/help-articles/use-article-link-pills"
import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { toast } from "@/components/toast"
import { TranslationOverlay } from "@/components/translation-overlay"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { useAuthStateData } from "@/lib/states/auth-state"
import { useIsTranslationRunning } from "@/lib/translation-status"
import { buildTranslationTargets } from "@/lib/translation-targets"
import { useColorSeedLookup, useJoinDoc } from "@/lib/use-doc-presence"
import { useHelpCenterArticleTranslations } from "@/lib/use-item-translations"
import { useRole } from "@/lib/useRole"
import { useSavableForm } from "@/lib/useSavableForm"
import { shouldCreateAutoVersion } from "@/lib/versioning"
import {
	mutateOk,
	reportServerRejection,
	useAuthData,
	useZero,
	useZeroWorkspace,
} from "@/lib/zero"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { CopilotOverlay } from "../threads/components/right-bar/ai-chat/copilot-overlay"
import { useSidebarAiChatState } from "../threads/components/right-bar/ai-chat/sidebar-ai-chat-state"
import { ArticlePreview } from "./article-preview"
import { showsCoverImage, showsSummary } from "./article-visibility"
import { DocsHoverNav } from "./docs-hover-nav"
import { useHelpCenterArticleContext } from "./help-center-article-context"
import {
	helpCenterArticleFormSchema,
	storedCoverBlurhash,
} from "./help-center-article-schema"
import { useHelpCenterLanguage } from "./help-center-context"
import { HelpCenterHeader } from "./help-center-header"
import { HelpCenterSubpageCard } from "./help-center-subpage-card"
import {
	HelpCenterArticleSummaryWithTranslation,
	HelpCenterArticleTitleWithTranslation,
} from "./help-center-title-with-translation"
import { HiddenArticleBanner } from "./hidden-article-banner"
import { RestorePreviewBanner } from "./restore-preview-banner"

type HelpCenterArticleWithTranslations = HelpCenterArticle & {
	translations?: readonly HelpCenterArticleTranslation[]
}

type HelpCenterGroupWithTranslations = HelpCenterGroup & {
	helpCenterArticles: readonly (HelpCenterArticleWithTranslations & {
		subpages?: readonly HelpCenterArticleWithTranslations[]
	})[]
	translations?: readonly HelpCenterGroupTranslation[]
}

const articlePlaceholder =
	"Start writing and press '/' to add images, videos, or PDFs ..."

interface HelpCenterArticleProps {
	group: HelpCenterGroupWithTranslations
	article: HelpCenterArticleWithTranslations
	// Shown beside an expanded assistant chat, which is already the copilot.
	embedded?: boolean
}
export const HelpCenterArticleView = forwardRef<
	HTMLDivElement,
	HelpCenterArticleProps
>(({ group: _group, article, embedded = false }, ref) => {
	const zero = useZero()

	const workspace = useZeroWorkspace()
	const navigate = useNavigate()
	const decodedToken = useAuthData()
	const session = useAuthStateData()
	const { previewVersion, setPreviewVersion } = useHelpCenterArticleContext()
	const { selectedLanguage, setSelectedLanguage } = useHelpCenterLanguage()

	const { selectedTranslation, defaultLanguage } =
		useHelpCenterArticleTranslations(article, selectedLanguage)
	const presenceKey = `article:${article.id}:${selectedLanguage.code}` as const
	useJoinDoc(presenceKey)
	const colorSeed = useColorSeedLookup()
	const sharedYdocRef = useRef<Y.Doc | null>(null)
	const seededHtmlRef = useRef<string | null>(null)

	const [subpages] = useQuery(queries.helpCenterArticleSubpages(article.id))

	const translationTargets = useMemo(
		() =>
			buildTranslationTargets({
				enabledLanguages: workspace?.enabledLanguages ?? [],
				defaultLanguage,
				translations: article.translations,
				hasContent: (row) =>
					!!row &&
					(!!row.title?.trim() ||
						(typeof row.content === "string" && row.content.trim().length > 0)),
			}),
		[workspace?.enabledLanguages, defaultLanguage, article.translations],
	)

	useEffect(() => {
		setPreviewVersion(null)
	}, [article.id, setPreviewVersion])

	useEffect(() => {
		lastSavedVersion.current = null
	}, [article.id, selectedLanguage.code])

	const { role } = useRole()

	const docsNavStore = useDocNavStore()
	useEffect(() => {
		docsNavStore.lastOpenedArticleId = article.id
	}, [article, docsNavStore])

	const tiptapRef = useRef<{ getEditorInstance: () => Editor } | null>(null)
	const articleLinkPills = useArticleLinkPills()
	const defaultLanguageRef = useRef(defaultLanguage)

	useEffect(() => {
		defaultLanguageRef.current = defaultLanguage
	}, [defaultLanguage])

	// A ref, not state: the autosave timer holds an older render's closure, and a
	// stale `null` here made every autosave write a brand-new version row.
	const lastSavedVersion = useRef<{
		title: string
		content: string
		imageUrl: string | null
	} | null>(null)

	// Zero resolves the optimistic client result first, so a save the server
	// refuses looks saved until reload. Report it once per editor session.
	const hasReportedRejection = useRef(false)
	const reportSaveRejected = useCallback(
		(_message: string, error: Error) => {
			if (hasReportedRejection.current) return
			hasReportedRejection.current = true
			captureError(error, {
				domain: "general",
				action: "article save rejected by server",
				workspaceId: workspace?.id,
				userId: decodedToken.userId,
				extra: { articleId: article.id },
			})
			toast.error({
				title: "Your article edits didn't save",
				description:
					"Reload the page to get the newest version, then apply your change again.",
				icon: BookOpenIcon,
			})
		},
		[article.id, decodedToken.userId, workspace?.id],
	)

	const [editorKey, setEditorKey] = useState(
		`editor-${article.id}-${selectedLanguage.code}`,
	)
	const [isEditorMounting, setIsEditorMounting] = useState(false)
	const editorBaselineHtml = useRef<string | null>(null)

	useEffect(() => {
		const newKey = `editor-${article.id}-${selectedLanguage.code}`
		if (newKey !== editorKey) {
			setIsEditorMounting(true)
			editorBaselineHtml.current = null
			const timeout = setTimeout(() => {
				setEditorKey(newKey)
				setIsEditorMounting(false)
			}, 50)
			return () => clearTimeout(timeout)
		}
	}, [article.id, selectedLanguage.code, editorKey])

	const { form, reset, save, isDirty, isDataSynced } =
		useSavableForm<HelpCenterArticleFormData>({
			schema: helpCenterArticleFormSchema,
			defaultValues: {
				translationId: selectedTranslation.id,
				languageCode: selectedLanguage.code,
				title: selectedTranslation.title ?? "",
				summary: selectedTranslation.summary ?? "",
				content: selectedTranslation.content ?? "",
				imageUrl:
					selectedTranslation.imageUrl ?? article?.imageUrl ?? undefined,
				imageBlurhash: storedCoverBlurhash(article, selectedTranslation),
			},
			saveFunc: async (formValues) => {
				if (!article) throw new Error("Failed to get article")
				if (!workspace) throw new Error("Failed to get workspace")

				const { translationId, languageCode, ...rest } = formValues

				if (sharedYdocRef.current) {
					rememberSaved(sharedYdocRef.current, rest.content)
				}

				const isNewTranslation = translationId.startsWith("temp-")
				const finalTranslationId = isNewTranslation
					? generateId()
					: translationId

				await mutateOk(
					reportServerRejection(
						zero.mutate(
							mutators.helpCenterArticleTranslations.upsert({
								id: finalTranslationId,
								workspaceId: workspace.id,
								language: languageCode,
								helpCenterArticleId: article.id,
								title: rest.title,
								summary: rest.summary || "",
								content: rest.content,
								imageUrl: rest.imageUrl,
							}),
						),
						reportSaveRejected,
					),
				)

				if (languageCode === defaultLanguageRef.current) {
					await mutateOk(
						reportServerRejection(
							zero.mutate(
								mutators.helpCenterArticles.update({
									id: article.id,
									title: rest.title,
									summary: rest.summary || "",
									content: rest.content,
									imageUrl: rest.imageUrl ?? null,
									imageBlurhash: rest.imageBlurhash ?? null,
								}),
							),
							reportSaveRejected,
						),
					)
				}

				if (
					shouldCreateAutoVersion({
						currentTitle: rest.title,
						currentContent: rest.content,
						currentImageUrl: rest.imageUrl ?? null,
						lastSavedVersion: lastSavedVersion.current,
					})
				) {
					// Capture the steps that produced the new content from the
					// previously-saved snapshot. The version row stores them so
					// pending proposals can rebase via real OT instead of
					// re-synthesizing a transform from snapshot diffs.
					const appliedSteps = lastSavedVersion.current
						? computeProposalSteps(
								lastSavedVersion.current.content,
								rest.content,
							)
						: undefined
					await mutateOk(
						zero.mutate(
							mutators.helpCenterArticles.createVersion({
								id: generateId(),
								articleId: article.id,
								title: rest.title,
								content: rest.content,
								imageUrl: rest.imageUrl ?? null,
								imageBlurhash: rest.imageBlurhash ?? null,
								summary: rest.summary || "",
								icon: article.icon,
								language: languageCode,
								appliedSteps: appliedSteps as JSONValue | undefined,
							}),
						),
					)

					lastSavedVersion.current = {
						title: rest.title,
						content: rest.content,
						imageUrl: rest.imageUrl ?? null,
					}
				}

				return formValues
			},
			onFail: (_, error) => {
				console.error("Updating docs article failed", error)
				captureError(error, {
					domain: "general",
					action: "update help center article",
					workspaceId: workspace?.id,
					userId: decodedToken.userId,
					extra: { articleId: article?.id },
				})
				toast.error({ title: "Failed to save article" })
			},
			autoSave: true,
		})

	useEffect(() => {
		form.reset({
			translationId: selectedTranslation.id,
			languageCode: selectedLanguage.code,
			title: selectedTranslation.title ?? "",
			summary: selectedTranslation.summary ?? "",
			content: selectedTranslation.content ?? "",
			imageUrl: selectedTranslation.imageUrl ?? article?.imageUrl ?? undefined,
			imageBlurhash: storedCoverBlurhash(article, selectedTranslation),
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTranslation.id, selectedLanguage.code])

	const titleRef = useRef<HTMLTextAreaElement | null>(null)
	const { ref: registerTitleRef, ...titleRegisterForm } = form.register("title")

	//? If the article were created 3 seconds ago, autofocus the title
	const isNewlyCreated = article
		? Date.now() - new Date(article?.createdAt).getTime() < 3000
		: false

	useEffect(() => {
		if (isNewlyCreated && titleRef.current) {
			titleRef.current.focus()
		}
	}, [isNewlyCreated])

	// While a background translation is in flight the row is an empty placeholder;
	// don't reset the form from it or it wipes the page. Content syncs on COMPLETED.
	const isTranslationInFlight = useIsTranslationRunning(selectedTranslation)

	// Everyone with this article open edits one live doc, like Notion or Linear.
	// Saving still goes through the form below, so the database stays the source.
	const sharedDoc = useSharedDoc({
		name: docsCollabName(article.id, selectedLanguage.code),
		token: session?.liveToken,
		enabled: !previewVersion && !isTranslationInFlight,
	})
	const sharedYdoc = sharedDoc.status === "ready" ? sharedDoc.ydoc : null
	sharedYdocRef.current = sharedYdoc

	useEffect(() => {
		seededHtmlRef.current = null
	}, [sharedYdoc])

	useEffect(() => {
		if (!article || !isDataSynced || isDirty || isTranslationInFlight) return
		reset({
			translationId: selectedTranslation.id,
			languageCode: selectedTranslation.language,
			title: selectedTranslation.title ?? "",
			summary: selectedTranslation.summary ?? "",
			content: selectedTranslation.content ?? "",
			imageUrl: selectedTranslation.imageUrl ?? article.imageUrl ?? undefined,
			imageBlurhash: storedCoverBlurhash(article, selectedTranslation),
		})
	}, [
		article,
		isDataSynced,
		isDirty,
		isTranslationInFlight,
		reset,
		selectedTranslation,
	])

	// Push content that changed outside the editor (an AI translation, an API or
	// MCP edit) into the live editor. Only unsaved local edits hold it back: a
	// merely focused editor with nothing to lose used to keep the stale text and
	// then write it back over the newer version.
	useEffect(() => {
		if (isDirty || isEditorMounting || isTranslationInFlight) return
		const editor = tiptapRef.current?.getEditorInstance()
		if (!editor) return

		const incoming =
			typeof selectedTranslation.content === "string"
				? selectedTranslation.content
				: ""
		if (incoming === editor.getHTML()) return

		if (sharedDoc.status === "ready") {
			// Before the seed this would write the body a second time next to it.
			if (!isSeeded(sharedDoc.ydoc)) return
			if (incoming === seededHtmlRef.current) return
			if (wasSavedBySession(sharedDoc.ydoc, incoming)) return
			const connected = sharedDoc.provider.awareness?.getStates().keys() ?? []
			if (!isSessionLeader(sharedDoc.ydoc, connected)) return
		}

		editor.commands.setContent(incoming)
		editorBaselineHtml.current = incoming
	}, [
		selectedTranslation.content,
		isDirty,
		isEditorMounting,
		isTranslationInFlight,
		sharedDoc,
	])

	// Copilot "Apply": replace the article body with the proposed content. We
	// also push it into the savable form and mark it dirty, because Tiptap's
	// setContent does not emit an update, so the editor's onUpdate (which is
	// what normally syncs + dirties the form) never fires. Without this the
	// visible editor changes but the change is never persisted.
	const pendingContentRewrite = useSidebarAiChatState(
		(s) => s.pendingContentRewrite,
	)
	const clearPendingContentRewrite = useSidebarAiChatState(
		(s) => s.setPendingContentRewrite,
	)
	useEffect(() => {
		if (pendingContentRewrite?.target !== "article") return
		const editor = tiptapRef.current?.getEditorInstance()
		if (!editor) return
		const html = pendingContentRewrite.html
		editor.commands.setContent(html)
		editorBaselineHtml.current = html
		form.setValue("content", html, { shouldDirty: true })
		clearPendingContentRewrite(null)
	}, [pendingContentRewrite, clearPendingContentRewrite, form])

	const getCopilotPageContext = useCallback(
		() => ({
			title: article?.title ?? undefined,
			html: tiptapRef.current?.getEditorInstance().getHTML(),
		}),
		[article?.title],
	)

	const scrollToHeadline = (key: string) => {
		const editorElement = tiptapRef.current?.getEditorInstance().view.dom
		if (!editorElement) return

		const headlines = [...(editorElement.querySelectorAll("h1,h2,h3") ?? [])]
		for (let i = 0; i < headlines.length; i++) {
			const h1 = headlines[i]
			if (!h1?.textContent) continue
			const h1Key = generateHeadlineKey(h1.textContent, i)
			if (h1Key === key) {
				h1.scrollIntoView({
					behavior: "smooth",
					block: "start",
				})
				break
			}
		}
	}

	useEffect(() => {
		if (location.hash !== "") {
			scrollToHeadline(location.hash.slice(1))
		}
	}, [])

	// ESC key handler to navigate to /edit-docs
	useShortcut(
		"escape",
		() => {
			docsNavStore.clearOpenGroups()
			void navigate({ to: "/edit-docs" })
		},
		{
			preventDefault: true,
		},
	)

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isDirty) {
				event.preventDefault()
			}
		}

		window.addEventListener("beforeunload", handleBeforeUnload)

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload)
		}
	}, [isDirty])

	const blocker = useBlocker({
		shouldBlockFn: ({ current, next }) =>
			isDirty && current.pathname !== next.pathname,
		withResolver: true,
		enableBeforeUnload: false,
	})

	// Auto-save on navigation instead of prompting. save() no-ops only when
	// the form is invalid, so we proceed either way rather than trapping the
	// user behind a confirm modal.
	// Run once per blocked navigation: save() re-renders (trigger + reset),
	// which re-fires this effect and re-enters save() in a loop that freezes
	// the page. The ref gates the effect; the race caps how long a stalled
	// sync connection can hold navigation (the mutation is already queued
	// locally and pushes on reconnect).
	const blockerHandledRef = useRef(false)
	// The blocker can reset while save() runs; proceeding then throws.
	const blockerRef = useRef(blocker)
	blockerRef.current = blocker
	useEffect(() => {
		if (blocker.status !== "blocked") {
			blockerHandledRef.current = false
			return
		}
		if (blockerHandledRef.current) return
		blockerHandledRef.current = true
		const timeout = new Promise((resolve) => setTimeout(resolve, 2500))
		void Promise.race([save(), timeout]).finally(() => {
			if (blockerRef.current.status === "blocked") blockerRef.current.proceed()
		})
	}, [blocker, save])

	if (!article || !workspace) {
		return null
	}

	return (
		<div className="flex h-full min-w-0" ref={ref}>
			<div className="relative flex min-w-0 flex-1 flex-col">
				{isTranslationInFlight && (
					<TranslationOverlay
						languageName={selectedLanguage.name}
						className="inset-x-0 bottom-0 top-12"
					/>
				)}

				<div className="flex-shrink-0">
					<HelpCenterHeader
						article={article}
						selectedLanguage={selectedLanguage}
						setSelectedLanguage={setSelectedLanguage}
						translationTargets={translationTargets}
						hideCopilot={embedded}
					/>
				</div>

				{article.visibility !== "PUBLIC" && (
					<HiddenArticleBanner visibility={article.visibility} />
				)}

				{previewVersion && (
					<RestorePreviewBanner
						articleId={article.id}
						version={previewVersion}
						defaultLanguage={defaultLanguage}
					/>
				)}

				{previewVersion ? (
					<div className="scroll-container docs-scroller flex min-h-0 flex-1 flex-row gap-10 overflow-y-auto pt-8">
						<ArticlePreview
							version={previewVersion}
							key={`preview-${previewVersion.id}`}
						/>
					</div>
				) : (
					<div
						className={cn(
							"scroll-container docs-scroller flex min-h-0 flex-1 flex-row gap-10 px-6 pt-14 [container-type:inline-size]",
							isTranslationInFlight ? "overflow-hidden" : "overflow-y-auto",
						)}
						key={editorKey}
					>
						<div className="docs-table-room flex min-w-0 flex-1">
							<div
								className={cn(
									"mx-auto flex min-w-0 max-w-[652px] flex-1 flex-col",
									{
										"pointer-events-none select-none": isTranslationInFlight,
									},
								)}
							>
								<div>
									<NucleoFillIconPicker
										disabled={role === "VIEWER"}
										buttonVariant="link"
										icon={article.icon}
										className="h-[1.1rem] min-h-[1rem] w-[1rem] min-w-[1.1rem] text-gray-700 opacity-100 dark:text-gray-300"
										buttonClassName="flex h-8 w-8 items-center justify-center rounded bg-gray-100 transition-colors hover:bg-gray-150 dark:bg-gray-800 dark:hover:bg-gray-750"
										onChange={async (icon) => {
											if (!icon) return

											await mutateOk(
												zero.mutate(
													mutators.helpCenterArticles.update({
														id: article.id,
														icon,
													}),
												),
											)
											analytics.track("update-helpcenterarticleicon", {
												id: article.id,
											})
										}}
									/>
									<Textarea
										{...titleRegisterForm}
										ref={(el) => {
											registerTitleRef(el)
											titleRef.current = el
										}}
										variant="borderless"
										noResize
										textSize="xxl"
										placeholder="New article"
										className={cn(
											"no-scrollbar mt-4 w-full bg-transparent",
											showsSummary(article.visibility) ? "mb-2" : "mb-0",
										)}
										style={{ lineHeight: "1.2" }}
										readOnly={role === "VIEWER"}
										fontWeight="bold"
										maxLength={200}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												if (!tiptapRef.current) return
												e.preventDefault()
												tiptapRef.current
													.getEditorInstance()
													.commands.focus("start")
											}
										}}
									/>
									{showsSummary(article.visibility) && (
										<Textarea
											{...form.register("summary")}
											variant="borderless"
											noResize
											textSize="md"
											placeholder="Write a summary..."
											className="mb-7 flex w-full flex-col bg-transparent text-gray-600 dark:text-gray-350"
											readOnly={role === "VIEWER"}
											maxLength={ARTICLE_SUMMARY_MAX_LENGTH}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													if (!tiptapRef.current) return

													e.preventDefault()
													tiptapRef.current
														.getEditorInstance()
														.commands.focus("start")
												}
											}}
										/>
									)}
									{showsCoverImage(article.visibility) && (
										<div className="mt-7">
											<CoverImage
												globalPaste
												articleId={article.id}
												imageUrl={form.watch("imageUrl") ?? null}
												blurhash={form.watch("imageBlurhash") ?? null}
												placeholder={
													<DefaultArticleCover
														icon={article.icon}
														logoUrl={workspace?.logoUrl}
														workspaceName={workspace?.name}
														accent={workspace?.accentColor}
													/>
												}
												onUpdate={async ({ imageUrl, blurhash }) => {
													const restored =
														imageUrl === null &&
														isArticleCoverHidden(form.getValues("imageUrl"))
													if (restored) {
														analytics.track(
															"restore-helpcenter-article-cover",
															{
																id: article.id,
															},
														)
													} else if (
														imageUrl === null ||
														isArticleCoverHidden(imageUrl)
													) {
														toast.success({ title: "Cover image removed" })
														analytics.track("unset-helpcenter-article-image", {
															id: article.id,
															generated: isArticleCoverHidden(imageUrl),
														})
													} else {
														analytics.track("upload-helpcenter-article-image", {
															id: article.id,
														})
													}

													form.setValue("imageUrl", imageUrl ?? undefined, {
														shouldDirty: true,
													})

													form.setValue(
														"imageBlurhash",
														blurhash ?? undefined,
														{
															shouldDirty: true,
														},
													)

													await save()
												}}
											/>
										</div>
									)}
									{form.formState.errors.title && (
										<p className="mb-2 text-sm text-red-500">
											{form.formState.errors.title.message}
										</p>
									)}
								</div>

								{isEditorMounting || sharedDoc.status === "connecting" ? (
									// The stored HTML is already local; show it while the live doc joins.
									<DefaultEditor
										key={`pending-${editorKey}`}
										extensions={[
											OrderedList.configure({
												HTMLAttributes: { class: "steps-list" },
											}),
											articleLinkPills,
										]}
										autofocus={false}
										initialContent={selectedTranslation.content || ""}
										placeholder={articlePlaceholder}
										onUpdate={() => undefined}
										editable={false}
										className={cn(
											"w-full max-w-[min(652px,100%)] flex-grow [&_.ProseMirror]:pb-72",
											showsSummary(article.visibility) ? "mt-8" : "mt-0",
										)}
									/>
								) : (
									<DefaultEditor
										key={sharedYdoc ? `live-${sharedYdoc.guid}` : "local"}
										collaborative={!!sharedYdoc}
										extensions={[
											OrderedList.configure({
												HTMLAttributes: {
													class: "steps-list",
												},
											}),
											articleLinkPills,
											ShiftEnterParagraph,
											...(sharedDoc.status === "ready"
												? sharedDocExtensions({
														ydoc: sharedDoc.ydoc,
														provider: sharedDoc.provider,
														user: {
															userId: decodedToken.userId,
															name: session?.name || "A teammate",
															color: cursorColor(
																colorSeed(decodedToken.userId),
															),
														},
													})
												: [
														RemoteCursors.configure({
															getDocKey: () => presenceKey,
															getSelfUserId: () => decodedToken.userId,
															getColorSeed: colorSeed,
														}),
													]),
										]}
										onCreate={({ editor }) => {
											if (!sharedYdoc) return
											const html = selectedTranslation.content || ""
											const seedId = seedSharedDoc(
												sharedYdoc,
												editor.schema,
												html,
											)
											if (seedId === null) return
											seededHtmlRef.current = html
											const stop = watchSeedRace(sharedYdoc, seedId)
											editor.on("destroy", stop)
										}}
										autofocus={false}
										initialContent={selectedTranslation.content || ""}
										placeholder={articlePlaceholder}
										onUpdate={({ editor, transaction }) => {
											const html = editor.getHTML()
											if (isRemoteChange(transaction)) {
												editorBaselineHtml.current = html
												form.setValue("content", html)
												return
											}
											// Tiptap re-emits normalized content right after mounting;
											// treat the first emission as the baseline so a freshly
											// loaded article isn't marked dirty.
											if (editorBaselineHtml.current === null) {
												editorBaselineHtml.current = html
												return
											}
											if (html === editorBaselineHtml.current) return
											form.setValue("content", html, {
												shouldDirty: true,
											})
											if (isDiscreteEditorChange(transaction)) void save()
										}}
										editable={role !== "VIEWER"}
										className={cn(
											"w-full max-w-[min(652px,100%)] flex-grow [&_.ProseMirror]:pb-72",
											showsSummary(article.visibility) ? "mt-8" : "mt-0",
										)}
										ref={tiptapRef}
									/>
								)}
								{subpages.length > 0 && (
									<div className="mt-8 grid grid-cols-1 gap-3 pb-72 sm:grid-cols-2 lg:grid-cols-3">
										{subpages.map((subpage) => (
											<HelpCenterSubpageCard
												key={subpage.id}
												article={subpage}
												titleComponent={
													<HelpCenterArticleTitleWithTranslation
														article={subpage}
														selectedLanguage={selectedLanguage}
													/>
												}
												summaryComponent={
													<HelpCenterArticleSummaryWithTranslation
														article={subpage}
														selectedLanguage={selectedLanguage}
													/>
												}
											/>
										))}
									</div>
								)}
							</div>
						</div>
						{/* Sidebars and the copilot shrink this pane, so the viewport says nothing about the room left. */}
						<DocsHoverNav
							editorKey={editorKey}
							content={selectedTranslation.content || ""}
							className="sticky top-0 order-3 h-fit shrink-0 pt-2 [@container(max-width:56rem)]:!hidden"
						/>
					</div>
				)}
			</div>

			{!embedded && (
				<CopilotOverlay
					layout="push"
					scope={{ kind: "article", articleId: article.id }}
					getPageContext={getCopilotPageContext}
					contextTitle={article.title ?? undefined}
					contextIcon={article.icon}
				/>
			)}
		</div>
	)
})
